"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect, useMemo } from "react";
import Loader from "../Loader";

type Category = {
  id: string;
  name: string;
  image?: string;
  discount?: number;
  smDesc?: string;
  badge?: string;
  color?: string;
  createdAt?: string;
};

type ShopCategoryProps = {
  spacing?: string;
  categories?: Category[];
};

const ShopCategory = ({ spacing = "100px 0", categories = [] }: ShopCategoryProps) => {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sortedCategories = useMemo(() => {
    return [...categories].sort(
      (a, b) =>
        new Date(a.createdAt || "").getTime() -
        new Date(b.createdAt || "").getTime()
    );
  }, [categories]);

  if (!sortedCategories || sortedCategories.length === 0) return null;

  return (
    <>
      {loading && <Loader />}

      <section
        style={{
          padding: isMobile ? "40px 0" : spacing,
          background: "linear-gradient(135deg, #FFF9F5 0%, #FFFBF8 50%, #FFF5EE 100%)",
          position: "relative",
          overflow: "hidden",
          marginTop: isMobile ? "-80px" : "-50px",
        }}
      >
        {/* DECORATIVE ELEMENTS */}
        <div
          style={{
            position: "absolute",
            top: -100,
            right: -150,
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, rgba(255,182,193,0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -80,
            left: -100,
            width: "350px",
            height: "350px",
            background: "radial-gradient(circle, rgba(255,215,180,0.12) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: isMobile ? "0 16px" : "0 60px",
            marginBottom: isMobile ? "0px" : "70px",
          }}
        >
          {/* HEADER */}
          <div style={{ textAlign: "center", marginBottom: isMobile ? "10px" : "70px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
              }}
            >
              <span style={{ fontSize: "24px" }}>🎁</span>
              <p
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  letterSpacing: "2.5px",
                  color: "#FF6B6B",
                  textTransform: "uppercase",
                }}
              >
                Perfect Gifts Await
              </p>
              <span style={{ fontSize: "24px" }}>✨</span>
            </div>

            <h2
              style={{
                fontSize: isMobile ? "32px" : "58px",
                fontWeight: 900,
                background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                marginBottom: "16px",
                lineHeight: 1.2,
              }}
            >
              Find The Perfect Gift
            </h2>

            <p
              style={{
                fontSize: isMobile ? "15px" : "19px",
                color: "#666",
                maxWidth: "650px",
                margin: "0 auto",
                lineHeight: 1.6,
              }}
            >
              From birthday surprises to wedding treasures – discover unique gifts for every occasion and everyone special
            </p>
          </div>

          {/* GRID */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
              gap: isMobile ? "14px" : "28px",
              marginBottom: isMobile ? "30px" : "60px",
            }}
          >
            {sortedCategories.map((item) => (
              <Link
                key={item.id}
                href={`/shop?category=${item.id}`}
                onClick={() => setLoading(true)}
                onMouseEnter={() => setHoveredCard(item.id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div
                  style={{
                    background: "#FFFFFF",
                    borderRadius: "24px",
                    padding: isMobile ? "18px 14px" : "36px 28px",
                    border: "2px solid #FFF0E6",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    boxShadow:
                      hoveredCard === item.id
                        ? "0 25px 60px rgba(255,107,107,0.18)"
                        : "0 8px 25px rgba(0,0,0,0.06)",
                    transform: hoveredCard === item.id ? "translateY(-10px) scale(1.02)" : "none",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {/* BADGE */}
                  {item.badge && (
                    <div
                      style={{
                        position: "absolute",
                        top: "14px",
                        right: "14px",
                        padding: "7px 16px",
                        background: item.color || "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
                        color: "#fff",
                        borderRadius: "50px",
                        fontSize: "11px",
                        fontWeight: 800,
                        boxShadow: "0 4px 12px rgba(255,107,107,0.3)",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {item.badge}
                    </div>
                  )}

                  {/* IMAGE */}
                  <div
                    style={{
                      width: "100%",
                      height: isMobile ? "85px" : "150px",
                      borderRadius: "16px",
                      overflow: "hidden",
                      background: "linear-gradient(135deg, #FFF5F0 0%, #FFE9E0 100%)",
                      marginBottom: "18px",
                      position: "relative",
                      border: "2px solid #FFF0E6",
                      transform: hoveredCard === item.id ? "scale(1.05)" : "scale(1)",
                      transition: "transform 0.4s ease",
                    }}
                  >
                    {item.image && (
                      <Image 
                        src={item.image} 
                        alt={item.name} 
                        fill 
                        style={{ 
                          objectFit: "cover",
                          filter: hoveredCard === item.id ? "brightness(1.05)" : "brightness(1)",
                          transition: "filter 0.3s ease",
                        }} 
                      />
                    )}
                  </div>

                  {/* TEXT */}
                  <h3 
                    style={{ 
                      fontSize: isMobile ? "15px" : "21px", 
                      fontWeight: 900,
                      color: "#2C2C2C",
                      marginBottom: "6px",
                    }}
                  >
                    {item.name}
                  </h3>

                  <p
                    style={{
                      fontSize: isMobile ? "12px" : "14px",
                      fontWeight: 600,
                      color: "#888",
                      marginBottom: "14px",
                      lineHeight: 1.4,
                    }}
                  >
                    {item.smDesc || "Discover amazing gifts"}
                  </p>

                  {/* CTA */}
                  <div
                    style={{
                      marginTop: "auto",
                      color: item.color || "#FF6B6B",
                      fontSize: isMobile ? "13px" : "17px",
                      fontWeight: 800,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span>Explore Gifts</span>
                    <span
                      style={{
                        transform: hoveredCard === item.id ? "translateX(5px)" : "translateX(0)",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* FOOTER CTA */}
          <div 
            style={{ 
              textAlign: "center", 
              background: "rgba(255,255,255,0.7)",
              backdropFilter: "blur(10px)",
              borderRadius: "20px",
              padding: "40px 30px",
              border: "2px solid #FFF0E6",
            }}
          >
            <p 
              style={{ 
                color: "#555", 
                marginBottom: "20px",
                fontSize: "17px",
                fontWeight: 600,
              }}
            >
              🎉 Can't find the perfect gift? Browse our complete collection!
            </p>

            <Link
              href="/shop"
              style={{
                display: "inline-block",
                padding: "16px 50px",
                background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
                color: "#fff",
                borderRadius: "50px",
                fontWeight: 800,
                textDecoration: "none",
                fontSize: "16px",
                boxShadow: "0 8px 25px rgba(255,107,107,0.35)",
                transition: "all 0.3s ease",
                letterSpacing: "0.5px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.boxShadow = "0 12px 35px rgba(255,107,107,0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(255,107,107,0.35)";
              }}
            >
              View All Gifts 🎁
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ShopCategory;